
import React, { useState, useEffect } from 'react';
import { Button, Col, FormGroup, Input, Modal, ModalBody, Progress } from 'reactstrap';
import { uploadData, isCancelError } from '@aws-amplify/storage';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getCurrentUser } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import { VideoLibraryAPI } from '../../helpers/firebase_helper';

const CreateJobModal = ({ isOpen, toggle }) => {
  const [jobName, setJobName] = useState('');
  const [workflow, setWorkflow] = useState('');
  const [videoName, setVideoName] = useState('');
  const [video, setVideo] = useState(null);
  const [libraryVideos, setLibraryVideos] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadTask, setUploadTask] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [showUpgradeButton, setShowUpgradeButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const profileData = useSelector((state) => state.Profile.profileData);
  const cognito_id = profileData?.result?.cognito_id;


  const fetchVideos = async () => {
    try {
      setLoading(true);


      const response = await VideoLibraryAPI.fetchLibraryData();
      const videos = response?.result?.files || [];
      setLibraryVideos(videos);
      return videos; // Return the videos array
    } catch (error) {
      console.error('Error fetching video data:', error);
      toast.error('Error fetching video data.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchVideos();
      setVideo(null);
      setVideoName('');
      setJobName('');
      setWorkflow('');
    }
  }, [isOpen]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileUpload = async (event) => {

    const file = event.target.files[0];
    if (!file) {
      return;
    }
    const userData = profileData?.result;

    if (!userData) {
      toast.error('Could not retrieve user profile data.');
      return;
    }

    const { storage_used, storage_quota, max_video_size } = userData;
    const remainingStorageBytes = storage_quota - storage_used;
    const fileSizeInBytes = file.size;
    const remainingStorageFormatted = formatBytes(remainingStorageBytes);
    const fileSizeFormatted = formatBytes(fileSizeInBytes);
    const maxVideoSizeFormatted = formatBytes(max_video_size);

    if (fileSizeInBytes > max_video_size) {
      toast.error(`The selected video exceeds the maximum allowed size of ${maxVideoSizeFormatted}. Please select a smaller file.`);
      return;
    }

    if (fileSizeInBytes > remainingStorageBytes) {
      toast.error(`Not enough storage available. Your remaining storage is ${remainingStorageFormatted} .`);

      setShowUpgradeButton(true);
      return;
    } else {
      setShowUpgradeButton(false);
    }
    try {
      const fileId = uuidv4();
      const fileNameWithId = `${fileId}-${file.name}`;
      const originalFileName = file?.name;
      const fileExtension = originalFileName.slice(((originalFileName.lastIndexOf(".") - 1) >>> 0) + 2);
      const fileType = file.type;
      let fileCategory = 'other';
      if (fileType.startsWith('video/')) fileCategory = 'video';
      else if (fileType.startsWith('image/')) fileCategory = 'image';
      else if (fileType.startsWith('audio/')) fileCategory = 'audio';
      else if (fileType === 'application/pdf' || fileType.includes('document')) fileCategory = 'document';

      setUploading(true);
      setUploadProgress(0);
      const controller = new AbortController();
      setAbortController(controller);

      const task = uploadData({
        path: () => `protected/${cognito_id}/${fileId}.${fileExtension}`,
        data: file,
        options: {
          contentType: fileType,
          metadata: {
            name: originalFileName,
          },
          onProgress: ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              const percentage = Math.round((transferredBytes / totalBytes) * 100);

              setUploadProgress(percentage);
            }
          },
          signal: controller.signal,
        },

      });
      setUploadTask(task);
      const result = await task.result;
      const apiData = {
        file_id: fileId,
        s3_key: result.path,
        filename: originalFileName,
        extension: fileExtension,
        content_type: fileType,
        file_type: fileCategory,
        size_bytes: fileSizeInBytes,
        folder_path: '/',
      };
      const result1 = await VideoLibraryAPI.createFileFolder(apiData);
      // console.log("result1", result1);
      
      // Extract the file_id from the API response
      const fileMetadata = result1?.result || result1?.data || result1;
      const uploadedFileId = fileMetadata?.file_id || fileId;
      
      // Refresh the library list to include the newly uploaded file
      const updatedVideos = await fetchVideos();
      
      // Find the complete video data from the library using file_id
      const completeVideoData = updatedVideos.find((vid) => vid.file_id === uploadedFileId);
      
      if (completeVideoData) {
        // Use the complete video data from library
        setVideo(completeVideoData);
        setVideoName(completeVideoData.filename || completeVideoData.video_title || originalFileName);
      } else {
        // If not found immediately, wait a bit and try again (in case of async processing)
        setTimeout(async () => {
          const retryVideos = await fetchVideos();
          const retryVideoData = retryVideos.find((vid) => vid.file_id === uploadedFileId);
          if (retryVideoData) {
            setVideo(retryVideoData);
            setVideoName(retryVideoData.filename || retryVideoData.video_title || originalFileName);
          } else {
            // Fallback: create video object from available data
            const videoData = {
              file_id: uploadedFileId,
              filename: originalFileName,
              cdn_url: fileMetadata?.cdn_url || fileMetadata?.file_url || '',
              s3_key: fileMetadata?.s3_key || result.path,
              extension: fileExtension,
              content_type: fileType,
              file_type: fileCategory,
              size_bytes: fileSizeInBytes,
            };
            setVideo(videoData);
            setVideoName(originalFileName);
          }
        }, 1000);
      }
      
      toast.success('Video uploaded successfully!');
    } catch (error) {
      if ((error.name === 'AbortError') || (isCancelError(error))) {

        toast.info('Upload canceled');
      } else {
        ;
        toast.error(error);
        if (error.message && error.message.includes('AccessDenied')) {
          console.error('Access denied. Please check your AWS Amplify configuration.');
        }
      }
    } finally {
      setUploading(false);
      setAbortController(null);
    }
  };

  const handleVideoSelect = (e) => {
    const selectedFileId = e.target.value;
    if (selectedFileId) {
      const selectedVideo = libraryVideos.find((vid) => vid.file_id === selectedFileId);
      setVideo(selectedVideo);
      setVideoName(selectedVideo.video_title);
    } else {
      setVideo(null);
      setVideoName('');
    }
  };

  const handleCancel = () => {
    if (uploadTask) {
      uploadTask.cancel();
      if (abortController) {
        abortController.abort();
      }
      setUploading(false);
    }
    setShowUpgradeButton(false);
    setVideoName('');
    setVideo(null);
    setJobName('');
    setWorkflow('');
    toggle();
  };

  const handleNext = (event) => {
    event.preventDefault();

    if (!jobName.trim()) {
      toast.error('Please enter a job name.');
      return;
    }
    if (!workflow) {
      toast.error('Please select a workflow.');
      return;
    }

    if (!video) {
      toast.error('Please select or upload a video before proceeding.');
      return;
    }
    if (workflow == "Transcode Wizard") {
      navigate('/video-processor/video-setting-trans', { state: { jobName, workflow, video } });
    }
    else {
      navigate('/video-processor/video-setting-single', { state: { jobName, workflow, video } });
    }

  };

  const handleUpgradeClick = () => {
    navigate('/billing/current-plan');
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalBody>
        <div className="job-container">
          <div className="d-flex flex-column align-items-center mb-4">
            <h3 className="mb-2">Create Job</h3>
            <h6 className="text-muted">Create a new job by entering the below details</h6>
          </div>

          <form>
            <div className="mb-3">
              <label htmlFor="jobName" className="form-label">
                <b>Job Name</b>
              </label>
              <input
                type="text"
                id="jobName"
                className="form-control"
                placeholder="Enter job name"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="workflow" className="form-label text-start">
                <b>Choose Workflow</b>
              </label>
              <select id="workflow"
                className="form-select"
                value={workflow}
                onChange={(e) => setWorkflow(e.target.value)}
              >
                <option value="">Select Workflow</option>
                <option>Transcode Wizard</option>
                {/* <option>Single Process</option> */}
              </select>
            </div>

            <div className="d-flex flex-column">
              <div className="d-flex justify-content-between mb-3">
                <label htmlFor="fileInput">
                  <button
                    type="button"
                    style={{ backgroundColor: '#eff4fd', height: '40px', width: '135px' }}
                    className="btn text-primary"
                    onClick={() => {
                      document.getElementById('fileInput').click();
                    }}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload Video'}

                  </button>
                </label>
                <input
                  id="fileInput"
                  type="file"
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />

                <span className="mx-3 fw-bold text-muted">OR</span>

                <Col md={6}>
                  <FormGroup>
                    <Input
                      type="select"
                      id="video"
                      name="video"
                      value={video?.file_id || ''}
                      onChange={handleVideoSelect}
                    >
                      <option value="">Select from Video Library</option>
                      {libraryVideos.map((vid, index) => (
                        <option key={index} value={vid.file_id}>
                          {vid.filename}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </div>
              {uploading && (
                <div className="progress-container mt-2">
                  <Progress animated value={uploadProgress} max="100">
                    {uploadProgress}%
                  </Progress>
                </div>
              )}
              {videoName && <p className="text-muted mt-2">Selected Video: {videoName}</p>}
            </div>

            <div className="d-flex justify-content-between mt-2">
              <button
                type="button"
                style={{ height: '40px', width: '135px' }}
                className="btn btn-outline-primary"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{ height: '40px', width: '205px' }}
                className="btn btn-success"
                onClick={handleNext}
              >
                Next
              </button>
              {showUpgradeButton && (
                <Button color="warning" onClick={handleUpgradeClick}>
                  Upgrade Plan
                </Button>
              )}
            </div>
          </form>
        </div>
      </ModalBody>
    </Modal>
  );
};

CreateJobModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};

export default CreateJobModal;
